import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Users, FileText, Calendar, Globe, Shield, Clock, Phone, Briefcase, GraduationCap, Heart, Plane, Building, Flag, MapPin, Search, Target, Send, Plus, AlertCircle, DollarSign, TrendingUp, Award, HelpCircle, Scale, X, CreditCard } from 'lucide-react';

const Australia = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <motion.section 
        className="bg-custom-pattern py-16 px-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-inter font-bold md:font-black text-acBlack leading-tight mb-6">
            Australia
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Practical guidance for work, study, family, visits, permanent residence, and citizenship.
          </p>
          
          <div className="bg-white rounded-lg shadow-lg p-8 text-left max-w-3xl mx-auto">
            <h2 className="text-2xl font-semibold text-acBlack mb-4">Snapshot</h2>
            <p className="text-gray-600 leading-relaxed">
              We help individuals and families plan, document, and file for Australia. We cover skilled and employer sponsored work visas, study and the Genuine Student requirement, family routes, visitor travel, permanent residence through employer or points pathways, and citizenship. Typical response time is 24 to 72 hours. London covers EMEA. New York runs an APAC desk with evening ET overlap.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Who this is for Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Who this is for
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Professionals</h3>
              <p className="text-gray-600 text-sm">Professionals with an Australian job offer or an internal transfer</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Students & Graduates</h3>
              <p className="text-gray-600 text-sm">Students and graduates planning a post study route where eligible</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Family Members</h3>
              <p className="text-gray-600 text-sm">Partners and children joining citizens, permanent residents, or eligible New Zealand citizens</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Plane className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Visitors</h3>
              <p className="text-gray-600 text-sm">Tourists and business visitors</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Flag className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Residents</h3>
              <p className="text-gray-600 text-sm">Residents preparing for permanent residence or citizenship</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* What we handle at a glance Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            What we handle at a glance
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Employment & Business</h3>
              <p className="text-gray-600 text-sm mb-4">TSS subclass 482, Employer Nomination Scheme 186, Skilled Employer Sponsored Regional 494, and skills assessments where required. Guidance on independent or state nominated skilled visas such as 189 and 190 and regional 491 where the profile fits</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Students & Graduates</h3>
              <p className="text-gray-600 text-sm mb-4">Student subclasses with Genuine Student requirement and OSHC, Temporary Graduate subclass 485 streams where eligible</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Family</h3>
              <p className="text-gray-600 text-sm mb-4">Partner visas onshore and offshore, Prospective Marriage subclass 300, child and dependent child routes, dependents of workers or students</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Visitors</h3>
              <p className="text-gray-600 text-sm mb-4">eVisitor, Electronic Travel Authority, and Visitor subclass 600 for tourism, family, or business visitor purposes</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Permanent Residence</h3>
              <p className="text-gray-600 text-sm mb-4">Transition from 482 or 494 to 186 where eligible, skilled independent and state or regional nominations, and family based permanent residence</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Citizenship</h3>
              <p className="text-gray-600 text-sm mb-4">By conferral for permanent residents who meet presence and character rules</p>
            </div>
          </div>
          <p className="text-center text-gray-600 mt-8">
            We do not handle protection visas or tribunal representation.
          </p>
        </div>
      </motion.section>

      {/* Outcomes Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Outcomes you can expect
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Written Plan</h3>
              <p className="text-gray-600 text-sm">Confirms the correct category, fees, realistic timelines, and key risks</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Document Pack</h3>
              <p className="text-gray-600 text-sm">Document pack and forms built to the rule text and current policy guidance</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Skills Assessment</h3>
              <p className="text-gray-600 text-sm">Skills assessment planning where required, state nomination steps if targeting points based routes, and employer sponsorship tasks for 482 or 186</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Application Support</h3>
              <p className="text-gray-600 text-sm">Online application, health and character checks, biometrics where requested, and interview preparation where relevant</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Status Management</h3>
              <p className="text-gray-600 text-sm">Clear rules on maintaining status during processing and a plan for renewals and permanent residence</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Employment and Skilled Routes Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Employment and skilled routes
          </h2>
          
          <div className="mb-12">
            <h3 className="text-2xl font-inter font-semibold text-acBlack text-center mb-8">Common pathways</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Temporary Skill Shortage 482</h4>
                <p className="text-gray-600 text-sm">Employer sponsored with nominated occupation on the list and market salary checks. Short term, medium term, and labour agreement streams</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Employer Nomination Scheme 186</h4>
                <p className="text-gray-600 text-sm">Direct entry where skills assessment and experience requirements are met, or transition from 482 after required service and occupation eligibility</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Skilled Employer Sponsored Regional 494</h4>
                <p className="text-gray-600 text-sm">Regional sponsorship with pathway to PR through subclass 191 where conditions are met</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Points Based Skilled Visas</h4>
                <p className="text-gray-600 text-sm">189, 190, 491 for eligible occupations with points on age, English, education, and experience. Often requires skills assessment and sometimes state or regional nomination</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-8 rounded-lg">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">What we do</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Occupation Mapping</h4>
                <p className="text-gray-600 text-sm">Map your role to an eligible occupation and confirm market salary and location rules</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Sponsor Coordination</h4>
                <p className="text-gray-600 text-sm">Coordinate sponsor steps for nomination, labour market checks if required, and position descriptions</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Assessment Planning</h4>
                <p className="text-gray-600 text-sm">Plan skills assessments, English tests, and police checks</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Application Support</h4>
                <p className="text-gray-600 text-sm">Prepare forms, lodge nominations and visas, and schedule health checks and biometrics where applicable</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Students and Graduates Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Students and graduates
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Student visas</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Genuine Student statement planning that links your background and future plans</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Funds in acceptable format, OSHC, and accommodation plan</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">App submission through ImmiAccount and timing around course start</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Temporary Graduate 485</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Stream selection, eligibility windows, and how it links to work and permanent residence planning</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Employer letters and degree evidence to support work rights during the graduate period</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Family Routes Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Family routes
          </h2>
          
          <div className="mb-12">
            <h3 className="text-2xl font-inter font-semibold text-acBlack text-center mb-8">Common pathways</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Partner & Prospective Marriage</h4>
                <p className="text-gray-600 text-sm">Onshore and offshore options with relationship evidence planning and sponsor checks. Sequencing for Prospective Marriage 300 to Partner 820 or 309 to 100</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Child & Dependent Child</h4>
                <p className="text-gray-600 text-sm">Birth or adoption documents, consent where one parent is not travelling, and health checks</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Dependents of Workers or Students</h4>
                <p className="text-gray-600 text-sm">Coordinated filings with the principal and rules on work and study for family</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Visitors Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Visitors
          </h2>
          
          <div className="bg-white p-8 rounded-lg shadow-sm border">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-6 text-center">Tourism and business visitor</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">eVisitor & ETA</h4>
                <p className="text-gray-600 text-sm">eVisitor and ETA for eligible passports and Visitor 600 where needed</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Documentation</h4>
                <p className="text-gray-600 text-sm">Purpose mapping, itinerary, invitation letters, and proof of funds</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Activity Guidance</h4>
                <p className="text-gray-600 text-sm">Guidance on permitted activities and length of stay</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Permanent Residence Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Permanent residence
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Employer pathways</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-acBlack mb-2">Transition from 482 or 494 to 186</h4>
                  <p className="text-gray-600 text-sm">Where criteria are met. Evidence of employment period, market salary, and occupation eligibility</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Points pathways</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-acBlack mb-2">Skilled Independent 189, State Nominated 190, Regional 491</h4>
                  <p className="text-gray-600 text-sm">With state nomination planning. Points optimisation through English tests, skills assessment timing, partner points, and regional strategies</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Citizenship Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Citizenship
          </h2>
          
          <div className="bg-white p-8 rounded-lg shadow-sm border">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-6 text-center">By conferral</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Residence Calculator</h4>
                <p className="text-gray-600 text-sm">Residence calculator, character checks, and language or knowledge test steps</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Application Process</h4>
                <p className="text-gray-600 text-sm">Application, interview, and ceremony planning</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Post-Ceremony</h4>
                <p className="text-gray-600 text-sm">Followed by passport steps</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

    
    </div>
  );
};

export default Australia;
